import DOMPurify from 'dompurify';

/**
 * Detect if content is HTML or plain text
 * @param {string} content - Content to check
 * @returns {boolean} - True if HTML, false if plain text
 */
export const isHtml = (content) => {
  if (!content) return false;
  
  // Check for common HTML tags
  const htmlPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlPattern.test(content);
};

/**
 * Convert plain text to HTML (for backwards compatibility with old posts)
 * @param {string} text - Plain text content
 * @returns {string} - HTML formatted content
 */
export const plainTextToHtml = (text) => {
  if (!text) return '';
  
  // Split by line breaks and wrap each line in <p> tags
  const lines = text.split('\n');
  const paragraphs = lines
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map(line => `<p>${escapeHtml(line)}</p>`);
  
  return paragraphs.join('');
};

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text
 */
const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - Raw HTML content
 * @returns {string} - Sanitized HTML
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';

  // Check if content is plain text (for backwards compatibility)
  if (!isHtml(html)) {
    // Convert plain text to HTML, preserving line breaks
    html = plainTextToHtml(html);
  }

  // Configure DOMPurify with allowed tags and attributes
  const config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'a', 'h1', 'h2', 'h3',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false
  };

  // Add hook to preserve Quill editor classes (ql-indent-*, ql-align-*, etc.)
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'class') {
      // Check if there are any Quill formatting classes
      const allClasses = data.attrValue.split(' ');
      const hasQuillClass = allClasses.some(cls => cls.startsWith('ql-'));
      
      if (hasQuillClass) {
        // Keep the entire class attribute if it contains any ql-* classes
        data.keepAttr = true;
      }
    }
  });

  // Sanitize and return
  const sanitized = DOMPurify.sanitize(html, config);
  
  // Remove the hook after use to avoid affecting other sanitizations
  DOMPurify.removeHook('uponSanitizeAttribute');

  // DEBUG: Check if indent classes are preserved
  if (/ql-indent-/.test(sanitized)) {
    console.log('✅ DEBUG: ql-indent classes preserved in sanitized HTML');
  } else {
    console.warn('⚠️ DEBUG: ql-indent classes MISSING in sanitized HTML');
    console.log('Original HTML included indent classes?', /ql-indent-/.test(html));
  }

  // Add rel="noopener noreferrer" to all external links for security
  const temp = document.createElement('div');
  temp.innerHTML = sanitized;
  
  const links = temp.querySelectorAll('a[href]');
  links.forEach(link => {
    const href = link.getAttribute('href');
    if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return temp.innerHTML;
};

/**
 * Extract plain text from HTML
 * @param {string} html - HTML content
 * @returns {string} - Plain text
 */
export const extractPlainText = (html) => {
  if (!html) return '';
  
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

/**
 * Get text length without HTML tags
 * @param {string} html - HTML content
 * @returns {number} - Character count
 */
export const getTextLength = (html) => {
  return extractPlainText(html).length;
};

/**
 * Truncate HTML to a specific length (for excerpts)
 * @param {string} html - HTML content
 * @param {number} maxLength - Maximum character length
 * @returns {string} - Truncated HTML
 */
export const truncateHtml = (html, maxLength = 200) => {
  const plainText = extractPlainText(html);
  
  if (plainText.length <= maxLength) {
    return html;
  }

  const truncated = plainText.substring(0, maxLength).trim() + '...';
  return `<p>${truncated}</p>`;
};
